import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Subject, switchMap, tap } from 'rxjs';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { InvalidValueMode } from '../../../../core/editable-fields/dropdown/dropdown.component';
import { FormatSpecifier, InputMode } from '../../../../core/editable-fields/text-input/text-input.component';
import { UtilityService } from '../../../../core/services/utility.service';
import { PeopleService } from '../../../../services/people.service';
import { SolarWindsService } from '../../../../services/solar-winds.service';

@Component({
    selector: 'msr-purchasing',
    templateUrl: './msr-purchasing.component.html',
    styleUrls: ['./msr-purchasing.component.scss'],
    standalone: false
})
export class MsrPurchasingComponent implements OnInit {
    public labelCols: number = 4;
    public requestTypes: any[] = ['Computer', 'Software', 'Other'];
    public swOsTypes: any[] = ['Windows', 'Apple', 'Platform Agnostic/Not Applicable'];
    public computerTypes: any[] = ['Performance Laptop', 'Custom Computer'];
    public computerOsTypes: any[] = ['Windows', 'Apple'];
    public request: any = {
        files: [],
        errors$: new BehaviorSubject({}),
        warnings$: new BehaviorSubject({}),
    }
    public isSubmitting: boolean = false;

    public submitTicket$: Subject<any> = new Subject<any>(); //  The object that is going to the API for submission

    public get invalidValueMode(): typeof InvalidValueMode {
        return InvalidValueMode;
    }

    public get inputMode(): typeof InputMode {
        return InputMode;
    }

    public get formatter(): typeof FormatSpecifier {
        return FormatSpecifier;
    }

    constructor(public peopleService: PeopleService,
        public toastr: ToastrService,
        public appService: SolarWindsService,
    ) {
        this.request.onChange = () => { this.request.IsDirty = true };
        this.request.validate = () => this.validate(this.request);
    }

    public ngOnInit(): void { }

    public validate(obj: any) {
        obj.errors = {};
        obj.warnings = {};
        obj.hasErrors = false;

        var requiredText = 'Required Field';

        if (!!obj.isOtherPurchase && !obj.otherPeople?.length)
            obj.errors['otherPeople'] = "At least one other person must be added.";

        if (!obj.reason?.trim())
            obj.errors['reason'] = requiredText;

        if (!obj.type)
            obj.errors['type'] = requiredText;

        if (obj.type == 'Computer') {
            obj.softwareTitle = undefined;
            obj.softwareVersion = undefined;
            obj.softwareOs = undefined;

            if (!obj.computerType)
                obj.errors['computerType'] = requiredText;

            if ((obj.computerType != 'Custom Computer') && !obj.computerOs)
                obj.errors['computerOs'] = requiredText;

            if (obj.computerType == 'Custom Computer'){
                obj.computerOs = undefined;
                obj.isItFunded = false;

                if (!obj.costCode?.trim())
                    obj.errors['costCode'] = requiredText;
            }
        }

        if (obj.type == 'Software') {
            obj.computerType = undefined;
            obj.computerOs = undefined;
            obj.isItFunded = undefined;

            if (!obj.softwareTitle?.trim())
                obj.errors['softwareTitle'] = requiredText;

            if (!obj.softwareOs)
                obj.errors['softwareOs'] = requiredText;
        }


        if ((obj.type == 'Other') && !obj.notes?.trim())
            obj.errors['notes'] = requiredText;

        obj.syncAlerts?.();

        if (Object.keys(obj.errors).length)
            obj.hasErrors = true;
    }

    public addFile(file: any) {
        if (file.file.size > UtilityService.maxFileSize)
            this.toastr.error('File is larger than 4MB and cannot be attached.');
        else {
            let displayName = file.file.name;
            if (displayName.length > 24) {
                let start = displayName.substring(0, 11);
                let end = displayName.substring(displayName.length - 13);
                displayName = `${start}...${end}`;
            }
            file.file.displayName = displayName;

            this.request.files.push(file.file);
        }
    }

    public removeFile(index: number) {
        this.request.files.splice(index, 1);
    }

    private resetForm() {
        this.request = {
            files: [],
            errors$: new BehaviorSubject({}),
            warnings$: new BehaviorSubject({}),
        }

        this.request.onChange = () => { this.request.IsDirty = true };
        this.request.validate = () => this.validate(this.request);
    }

    public submit() {
        this.isSubmitting = true;
        let formData = new FormData();
        let toSubmit:any = {
            type: this.request.type,
            otherPersonIds: this.request.isOtherPurchase
                ? this.request.otherPeople.map(p => p.personId).join('|')
                : '',
            reason: this.request.reason,
            notes: this.request.notes,
            costCode: this.request.costCode,
        }

        if (this.request.type == 'Computer') {
            toSubmit.computerType = this.request.computerType;

            if (this.request.computerType != 'Custom Computer') {
                toSubmit.computerOs = this.request.computerOs;
                toSubmit.isItFunded = this.request.isItFunded;
            }
        }

        if (this.request.type == 'Software') {
            toSubmit.softwareTitle = this.request.softwareTitle;
            toSubmit.softwareVersion = this.request.softwareVersion;
            toSubmit.softwareOs = this.request.softwareOs;
        }

        if (!!this.request.files?.length) {
            for (let file of this.request.files)
                formData.append('files', file, file.filename);
        }

        formData.append('request', JSON.stringify(toSubmit));

        this.submitTicket$.next(formData);
    }

    public purchase$ = this.submitTicket$.pipe(
        switchMap((data) => {
            return this.appService.purchase$(data)
        }),
        tap((result) => {
            if (result?.Id) {
                this.toastr.success("Purchase request successfully submitted.");
                this.resetForm();
            }
            else {
                let message = "There was an error submitting your request.";

                if (result.error?.Message)
                    message = result.error.Message;

                this.toastr.error(message);
            }

            this.isSubmitting = false;
        }
        ),
    )
}


